import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { ReactQueryDevtools } from 'react-query/devtools';
import { ErrorBoundary } from '@sentry/react';
import { PrivateRoute, useIsAuthenticated } from 'react-auth-kit';

import { CriticalErrorFallback } from 'src/layouts/error';
import AppShell from 'src/components/AppShell';

import { Login } from './pages/Login/Login';
import { SSOLogin } from './pages/Login/SSOLogin';
import { Dashboard } from './pages/Dashboard';
import { Collections } from './pages/Collections/Collections';
import { Modal as AssetModal } from './pages/Asset/Modal';
import { StorageSettings } from './admin-pages/StorageSettings/StorageSettings';
import { SecuritySettings } from './admin-pages/SecuritySettings/SecuritySettings';
import { ImageSettings } from './admin-pages/ImageSettings/ImageSettings';
import { WebhookSettings } from './admin-pages/WebhookSettings/WebhookSettings';
import { DatabaseSettings } from './admin-pages/DatabaseSettings/DatabaseSettings';
import { VideoSettings } from './admin-pages/VideoSettings/VideoSettings';
import { WatermarkSettings } from './admin-pages/WatermarkSettings/WatermarkSettings';
import { AddonsSettings } from './admin-pages/AddonsSettings/AddonsSettings';
import { AiSettings } from './admin-pages/AiSettings/AiSettings';
import { MigrationSettings } from './admin-pages/MigrationSettings/MigrationSettings';
import { AiUsage } from './pages/AiUsage/AiUsage';
import { AgentActivity } from './pages/AgentActivity/AgentActivity';
import DeliveryAnalytics from './pages/DeliveryAnalytics/DeliveryAnalytics';
import AnalyticsDestinations from './pages/AnalyticsDestinations/AnalyticsDestinations';
import { NotificationSettings } from './admin-pages/NotificationSettings/NotificationSettings';
import { MetadataSettings } from './admin-pages/MetadataSettings/MetadataSettings';
import { VersionSettings } from './admin-pages/VersionSettings/VersionSettings';
import { ToolsSettings } from './admin-pages/ToolsSettings/ToolsSettings';
import { ProcessingQueues } from './admin-pages/ProcessingQueues/ProcessingQueues';
import { SchemaSettings } from './admin-pages/SchemaSettings/SchemaSettings';
import Users from './admin-pages/Users/Users';
import { Subscription } from './admin-pages/Subscription/Subscription';
import { PlanPicker } from './admin-pages/Subscription/PlanPicker';
import SettingsRedirect from './components/SettingsRedirect/SettingsRedirect';
import Trash from './pages/Trash/Trash';
import Basket from './pages/Basket/Basket';
import FileSpinPickerProvider from './providers/FileSpinPickerProvider';
import Collection from './pages/Collection/Collection';
import Profile from './pages/Profile/Profile';
import Onboarding from './pages/Onboarding/Onboarding';
import OnboardingPreview from './pages/Onboarding/OnboardingPreview';
import { SignupRoute } from './pages/Signup/Signup';
import JobList from './pages/Jobs/List';
import JobStatus from './pages/Jobs/Status';
import NotificationsPage from './pages/Notifications';
import { MyApprovals, Reviews } from './pages/Workflows';
import CheckUsageComponent from './pages/CheckUsage/Component';
import { BulkDeleteConfirm } from './pages/BulkDeleteConfirm';
import { BulkDelete } from './pages/BulkDelete';
import { DetailsUsage } from './pages/DetailsUsage/Component';
import ReissueCallbacks from './pages/ReissueCallbacks/ReissueCallbacks';
import ReprocessAssets from './pages/ReprocessAssets/ReprocessAssets';
import GetAssetLinks from './pages/GetAssetLinks/GetAssetLinks';
import CdnPrefetchPurge from './pages/CdnPrefetchPurge/CdnPrefetchPurge';
import SharesList from './pages/Shares/SharesList';
import ShareDetails from './pages/Shares/ShareDetails';
import TemplatesList from './pages/Shares/TemplatesList';
import PublicShare from './pages/PublicShare';
import { UploadLink } from './pages/UploadLink/UploadLink';
import { loginPathFor } from './utils/nextPath';
import { ConfigProvider } from 'antd';
import { SchedulesList, ScheduleDetail, ScheduleForm, ScheduleRunReplay, AuthorizeBounce, OAuthCallback } from './pages/Schedules';
import { config } from './utils/config';

const Router = () => {
  const location = useLocation<any>();
  const isAuthenticated = useIsAuthenticated();
  let background = location?.state?.background;

  // Home route: everyone lands on /assets by default (admins included).
  const HomeRedirect = () => {
    return <Redirect to="/assets" />;
  };

  return (
    <>
      <ConfigProvider
        theme={{
          token: {
            // Seed Token
            colorPrimary: '#2e53e3',
            borderRadius: 10,
            fontWeightStrong: 700,
            // Alias Token
            colorBgContainer: '#2e53e3',
          },
        }}
      >
        <ErrorBoundary fallback={CriticalErrorFallback}>
          <Switch location={background || location}>
            <Route exact path="/login" component={Login} />
            {/* Social login return leg: /login/sso?code=..&next=..[&signup=1] | ?error=.. */}
            <Route exact path="/login/sso" component={SSOLogin} />

            {/* /signup: general signup (email / Google / Microsoft), or the
                demo-claim instant onboarding when the link carries demo_run_id + t */}
            <Route exact path="/signup" component={SignupRoute} />
            <Route exact path="/signup/:code" component={Onboarding} />
            <Route exact path="/onboarding" component={Onboarding} />
            <Route exact path="/onboarding/preview" component={OnboardingPreview} />

            <Route exact path="/bulk-purge-assets/confirm" component={BulkDeleteConfirm} />

          <Route
            path={[
              '/assets',
              '/asset/:id',
              '/collection',
              '/collections',
              '/collection/:id',
              '/shares',
              '/shares/templates',
              '/shares/:id',
              '/asset-transfers',
              '/settings',
              '/notifications',
              '/workflows/reviews',
              '/workflows/my-approvals',
              '/me',
              '/search',
              '/trash',
              '/basket',
              '/jobs',
              '/job/:id',
              '/usage',
              '/insights/ai-usage',
              '/insights/agent-activity',
              '/insights/delivery',
              '/insights/destinations',
              '/reissue-callbacks',
              '/reprocess-assets',
              '/get-asset-links',
              '/cdn-prefetch-purge',
              '/bulk-purge-assets',
              '/ai/schedules',
              '/ai/schedules/new',
              '/ai/schedules/authorize',
              '/ai/schedules/oauth-callback',
              '/ai/schedules/:id',
              '/ai/schedules/:id/edit',
              '/ai/schedules/:id/runs/:sessionId',
            ]}
          >
            <FileSpinPickerProvider>
              <AppShell>
                <Switch>
                  <PrivateRoute path="/assets" loginPath="/login" component={Dashboard} exact />
                  <PrivateRoute path="/asset/:id" loginPath="/login" component={AssetModal} exact />
                  <PrivateRoute path="/collection" loginPath="/login" component={() => <div>TBI</div>} exact />
                  <PrivateRoute path="/collections" loginPath="/login" component={Collections} exact />
                  <PrivateRoute path="/collection/:id" loginPath="/login" component={Collection} exact />
                  <PrivateRoute path="/shares" loginPath="/login" component={SharesList} exact />
                  <PrivateRoute path="/shares/templates" loginPath="/login" component={TemplatesList} exact />
                  <PrivateRoute path="/shares/:id" loginPath="/login" component={ShareDetails} exact />
                  <PrivateRoute path="/asset-transfers" loginPath="/login" component={() => <div>TBI</div>} exact />
                  <PrivateRoute path="/settings" loginPath="/login" component={() => <div>TBI</div>} exact />
                  <PrivateRoute path="/notifications" loginPath="/login" component={NotificationsPage} exact />
                  <PrivateRoute path="/workflows/reviews" loginPath="/login" component={Reviews} exact />
                  <PrivateRoute path="/workflows/my-approvals" loginPath="/login" component={MyApprovals} exact />
                  <PrivateRoute path="/me" loginPath="/login" component={Profile} exact />
                  <PrivateRoute path="/search" loginPath="/login" component={() => <div>TBI</div>} exact />
                  <PrivateRoute path="/trash" loginPath="/login" component={Trash} exact />
                  <PrivateRoute path="/basket" loginPath="/login" component={Basket} exact />
                  <PrivateRoute path="/jobs" loginPath="/login" component={JobList} exact />
                  <PrivateRoute path="/job/:id" loginPath="/login" component={JobStatus} exact />
                  <PrivateRoute path="/usage" loginPath="/login" component={CheckUsageComponent} exact />
                  <PrivateRoute path="/usage/details" loginPath="/login" component={DetailsUsage} />
                  <PrivateRoute path="/insights/ai-usage" loginPath="/login" component={AiUsage} exact />
                  <PrivateRoute path="/insights/agent-activity" loginPath="/login" component={AgentActivity} exact />
                  <PrivateRoute path="/insights/delivery" loginPath="/login" component={DeliveryAnalytics} exact />
                  <PrivateRoute path="/insights/destinations" loginPath="/login" component={AnalyticsDestinations} exact />
                  <PrivateRoute path="/reissue-callbacks" loginPath="/login" component={ReissueCallbacks} exact />
                  <PrivateRoute path="/reprocess-assets" loginPath="/login" component={ReprocessAssets} exact />
                  <PrivateRoute path="/get-asset-links" loginPath="/login" component={GetAssetLinks} exact />
                  <PrivateRoute path="/cdn-prefetch-purge" loginPath="/login" component={CdnPrefetchPurge} exact />
                  <PrivateRoute path="/bulk-purge-assets" loginPath="/login" component={BulkDelete} exact />
                  <PrivateRoute path="/ai/schedules/new" loginPath="/login" component={ScheduleForm} exact />
                  <PrivateRoute path="/ai/schedules/authorize" loginPath="/login" component={AuthorizeBounce} exact />
                  <PrivateRoute path="/ai/schedules/oauth-callback" loginPath="/login" component={OAuthCallback} exact />
                  <PrivateRoute path="/ai/schedules/:id/edit" loginPath="/login" component={ScheduleForm} exact />
                  <PrivateRoute path="/ai/schedules/:id/runs/:sessionId" loginPath="/login" component={ScheduleRunReplay} exact />
                  <PrivateRoute path="/ai/schedules/:id" loginPath="/login" component={ScheduleDetail} exact />
                  <PrivateRoute path="/ai/schedules" loginPath="/login" component={SchedulesList} exact />
                  {/* <PrivateRoute path="/usage/explore" loginPath='/login' component={ExploreUsageComponent} exact/> */}
                </Switch>
              </AppShell>
            </FileSpinPickerProvider>
          </Route>
          <Route path={['/admin/users']}>
            <AppShell>
              <Switch>
                <PrivateRoute path="/admin/users" loginPath="/login" component={Users} exact />
              </Switch>
            </AppShell>
          </Route>
          <Route path={['/admin/subscription']}>
            <AppShell>
              <Switch>
                <PrivateRoute path="/admin/subscription/plans" loginPath="/login" component={PlanPicker} exact />
                <PrivateRoute path="/admin/subscription" loginPath="/login" component={Subscription} exact />
              </Switch>
            </AppShell>
          </Route>
          <Route path={['/admin/schemas']}>
            <AppShell>
              <Switch>
                <PrivateRoute path="/admin/schemas" loginPath="/login" component={SchemaSettings} exact />
              </Switch>
            </AppShell>
          </Route>
          <Route path={['/admin/shares/templates']}>
            <AppShell>
              <Switch>
                <PrivateRoute path="/admin/shares/templates" loginPath="/login" component={TemplatesList} exact />
              </Switch>
            </AppShell>
          </Route>
          <Route path={['/admin/addons']}>
            <AppShell>
              <Switch>
                <PrivateRoute path="/admin/addons" loginPath="/login" component={AddonsSettings} exact />
              </Switch>
            </AppShell>
          </Route>
          {config.showTaskQueues && (
            <Route path={['/admin/processing-queues', '/admin/workflows-and-tasks']}>
              <AppShell>
                <Switch>
                  <PrivateRoute path="/admin/processing-queues" loginPath="/login" component={ProcessingQueues} exact />
                  {/* Renamed 2026-09-01: this page is Celery queue depths, and
                      the old path claimed the word "workflows" that the sidebar
                      Workflows section now owns. The redirect is permanent —
                      the old URL is in bookmarks and runbooks. */}
                  <Redirect from="/admin/workflows-and-tasks" to="/admin/processing-queues" />
                </Switch>
              </AppShell>
            </Route>
          )}
          {/* Migrating an account off Cloudinary / ImageKit: Configure >
              "Migrate to FileSpin". Its own page, not a Settings tab (see
              navItems.ts). Flag-gated because the screen is a dead end without
              the migration-onramp service behind it. */}
          {config.showMigration && (
            <Route path={['/admin/migrate']}>
              <AppShell>
                <Switch>
                  <PrivateRoute path="/admin/migrate" loginPath="/login" component={MigrationSettings} exact />
                </Switch>
              </AppShell>
            </Route>
          )}
          <Route path={['/admin']}>
            <AppShell title="Settings">
              <Switch>
                <PrivateRoute
                  path="/admin/settings/"
                  loginPath="/login"
                  component={SettingsRedirect}
                  exact
                />
                <PrivateRoute path="/admin/settings/storage" loginPath="/login" component={StorageSettings} exact />
                <PrivateRoute path="/admin/settings/security" loginPath="/login" component={SecuritySettings} exact />
                <PrivateRoute path="/admin/settings/image" loginPath="/login" component={ImageSettings} exact />
                <PrivateRoute path="/admin/settings/watermark" loginPath="/login" component={WatermarkSettings} exact />
                <PrivateRoute path="/admin/settings/video" loginPath="/login" component={VideoSettings} exact />
                <PrivateRoute path="/admin/settings/webhook" loginPath="/login" component={WebhookSettings} exact />
                <PrivateRoute path="/admin/settings/database" loginPath="/login" component={DatabaseSettings} exact />
                <PrivateRoute path="/admin/settings/metadata" loginPath="/login" component={MetadataSettings} exact />
                <PrivateRoute path="/admin/settings/versions" loginPath="/login" component={VersionSettings} exact />
                <PrivateRoute path="/admin/settings/ai" loginPath="/login" component={AiSettings} exact />
                <Route path="/admin/settings/ai-credits" exact>
                  <Redirect to="/insights/ai-usage" />
                </Route>
                <PrivateRoute path="/admin/settings/notifications" loginPath="/login" component={NotificationSettings} exact />
                <PrivateRoute path="/admin/settings/tools" loginPath="/login" component={ToolsSettings} exact />
                <PrivateRoute path="/admin/settings/processing-queues" loginPath="/login" component={ProcessingQueues} exact />
              </Switch>
            </AppShell>
          </Route>
          {/* Public share page - no auth required */}
          <Route path={['/share/:id', '/shared/:id']} component={PublicShare} />
          {/* Upload link an AI agent opened (mcp#131): signed in, no dashboard
              chrome. Signed-out users come back here after login. */}
          <Route
            path="/upload/:linkId"
            exact
            render={({ location: loc }) =>
              isAuthenticated() ? <UploadLink /> : <Redirect to={loginPathFor(loc.pathname + loc.search)} />
            }
          />
          <Route path="/" component={HomeRedirect} />
        </Switch>

          {background && <PrivateRoute path="/asset/:id" loginPath="/login" component={AssetModal} />}
          {background && (
            <PrivateRoute path="/usage/details/:userid/:user/:period" loginPath="/login" component={DetailsUsage} />
          )}
          {/* {background && <PrivateRoute path="/usage/explore" loginPath="/login" component={ExploreUsageComponent} />} */}
        </ErrorBoundary>
      </ConfigProvider>

      <ReactQueryDevtools
        initialIsOpen={false}
        position="bottom-right"
        toggleButtonProps={{ style: { display: 'none' } }}
      />
    </>
  );
};

export default Router;
